// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';
import { throttle } from 'lodash';

// Utils
import { hasChanged } from '../../utils/react/propsComparisons';

// Selectors
import { documentSelector } from './documentModeSelector';

// Actions
import { deselectAllElements } from '../../element/selection/selectionActions';

// Components
import MobileContent from './MobileContent';
import LegacyMobileBoardHeader from './LegacyMobileBoardHeader';
import MobileFloatingTool from './MobileFloatingTool';
import AppHeaderMobile from './AppHeaderMobile';
import AndroidStopEditingHelper from './android/AndroidStopEditingHelper';
import BoardPasswordProtectedContainer from '../passwordProtection/BoardPasswordProtectedContainer';
import { ZoomStateContextProvider } from '../../canvas/zoom/ZoomStateContext';
import mobileScrollPositionRetainDecorator from './mobileScrollPositionRetainDecorator';
import MobileBoardHeaderHybrid from './hybrid/MobileBoardHeaderHybrid';

// Types
import { LegacyHybridUseCase } from '../../../common/platform/platformTypes';

// Styles
import './LegacyMobileWorkspace.scss';

const mapDispatchToProps = (dispatch) => ({
    deselectAll: (event) => dispatch(deselectAllElements()),
});

const PAGE_TRANSITION_DUR = 400;
const CURRENT_BOARD_HEADER_HEIGHT = 64;

const currentBoardRootChanged = hasChanged('isCurrentBoardRoot');

const isBoardHeaderShown = (platformDetails) => {
    switch (platformDetails.legacyHybridUseCase) {
        case LegacyHybridUseCase.ANDROID_BOARD_LIST:
            return false;
        case LegacyHybridUseCase.IPAD_OS:
        default:
            return true;
    }
};

const getIsMobileFloatingToolShown = (platformDetails) => {
    switch (platformDetails.legacyHybridUseCase) {
        case LegacyHybridUseCase.IPAD_OS:
        case LegacyHybridUseCase.ANDROID_BOARD_LIST:
            return false;
        default:
            return true;
    }
};

// This is used to assist slide page transitions
const scrollPositions = new Map();

@mobileScrollPositionRetainDecorator(scrollPositions)
@connect(documentSelector, mapDispatchToProps)
class MobileWorkspace extends React.Component {
    constructor(props) {
        super(props);

        this.hasBodyClass = false;

        this.state = {
            pageTransition: 'slide-left',
        };
    }

    componentWillMount() {
        this.attachScrollListener();

        this.setRootBoardClass(this.props.isCurrentBoardRoot);
    }

    componentWillReceiveProps(nextProps) {
        // Hide current board header on root
        if (currentBoardRootChanged(this.props, nextProps)) {
            this.setRootBoardClass(nextProps.isCurrentBoardRoot);
        }

        // Updates the page transition to be left or right depending on the navigation
        if (nextProps.currentBoardId === this.props.currentBoardId) return;
        if (nextProps.breadcrumb.path === this.props.breadcrumb.path) return;

        const pageTransition =
            nextProps.breadcrumb.path.length >= this.props.breadcrumb.path.length ? 'slide-left' : 'slide-right';

        this.setState({ pageTransition });

        // Adjust the position of the workspace for the intermediate transition state.
        // In the transition state, the workspace scroll position resets to the top,
        // to accomodate this, the HTML element is given a calculated top padding.
        //
        // The result is that the board that is leaving and the board that is entering
        // stay in their current scroll position.
        if (this.nextBoard) {
            const scrollPositionForNextBoard = scrollPositions.get(nextProps.currentBoardId) ?? 0;
            const windowScrollY = window.scrollY ?? 0;

            const scrollPositionToSet =
                pageTransition == 'slide-left' ? -windowScrollY : scrollPositionForNextBoard - windowScrollY;
            const styleStr = `top: ${scrollPositionToSet}px;`;
            this.nextBoard.setAttribute('style', styleStr);
        }
    }

    componentWillUnmount() {
        this.removeScrollListener();
        this.setScrollBodyClass(false);
    }

    onScroll = throttle((event) => {
        this.setScrollBodyClass(window.scrollY > CURRENT_BOARD_HEADER_HEIGHT);
    }, 100);

    setRootBoardClass = (isRoot) => {
        isRoot ? document.body.classList.add('root-board') : document.body.classList.remove('root-board');
    };

    setScrollBodyClass = (enable) => {
        if (enable === this.hasBodyClass) return;

        this.hasBodyClass = enable;

        enable ? document.body.classList.add('mobile-scrolled') : document.body.classList.remove('mobile-scrolled');
    };

    attachScrollListener = (enable) => window.addEventListener('scroll', this.onScroll);

    removeScrollListener = (enable) => window.removeEventListener('scroll', this.onScroll);

    render() {
        const { currentBoardId, gridSize, deselectAll, rootBoardId, platformDetails } = this.props;
        const { pageTransition } = this.state;

        const getAppHeader = () => {
            switch (platformDetails.legacyHybridUseCase) {
                case LegacyHybridUseCase.ANDROID_BOARD_LIST:
                    return <MobileBoardHeaderHybrid {...this.props} />;
                case LegacyHybridUseCase.IPAD_OS:
                default:
                    return <AppHeaderMobile {...this.props} />;
            }
        };

        return (
            <div className="DocumentMode LegacyMobileWorkspace">
                <AndroidStopEditingHelper />
                {getAppHeader()}
                {getIsMobileFloatingToolShown(platformDetails) && <MobileFloatingTool {...this.props} />}

                <div className="content-wrapper">
                    <BoardPasswordProtectedContainer>
                        <ZoomStateContextProvider zoomScale={1}>
                            <CSSTransitionGroup
                                transitionName={pageTransition}
                                transitionEnterTimeout={PAGE_TRANSITION_DUR}
                                transitionLeaveTimeout={PAGE_TRANSITION_DUR}
                            >
                                <div
                                    className="content"
                                    key={currentBoardId}
                                    ref={(c) => {
                                        this.nextBoard = c;
                                    }}
                                    onClick={deselectAll}
                                >
                                    {rootBoardId && <div className="header-spacer" />}
                                    {isBoardHeaderShown(platformDetails) && <LegacyMobileBoardHeader {...this.props} />}
                                    <MobileContent
                                        {...this.props}
                                        currentBoardId={currentBoardId}
                                        gridSize={gridSize}
                                        legacyHybridUseCase={platformDetails.legacyHybridUseCase}
                                    />
                                </div>
                            </CSSTransitionGroup>
                        </ZoomStateContextProvider>
                    </BoardPasswordProtectedContainer>
                </div>
            </div>
        );
    }
}

MobileWorkspace.propTypes = {
    currentBoardId: PropTypes.string.isRequired,
    currentBoard: PropTypes.object.isRequired,
    isCurrentBoardRoot: PropTypes.bool,
    gridSize: PropTypes.number,
    isEditable: PropTypes.bool,
    breadcrumb: PropTypes.object,
    deselectAll: PropTypes.func,
    rootBoardId: PropTypes.string,
    breadcrumbObj: PropTypes.object,
    platformDetails: PropTypes.object,
};

export default MobileWorkspace;
